import type { Integration } from '../types/integration'
import type { IntegrationProviderId } from '../types/integration-provider-id'
import { resolveEquifaxBaseUrl } from './allowed-provider-urls'
import { integrationHasProvider } from './integration-providers'
import type { IEquifaxDitConfig } from '../equifax-dit-config'
import type { IEquifaxPrequalConfig } from '../equifax-prequal'
import type { IJackHenrySilverlakeIntegrationConfig } from '../types/jack-henry-silverlake'
import type { IJackHenrySynergyIntegrationConfig } from '../types/jack-henry-synergy'
import { JACK_HENRY_SYNERGY_OAUTH_SCOPE } from '../types/jack-henry-synergy'
import {
  JACK_HENRY_CORE_SERVICE_GATEWAY_BASE_URL,
  JACK_HENRY_CORE_TOKEN_URL,
  JACK_HENRY_SYNERGY_BASE_URL,
  JACK_HENRY_SYNERGY_IDENTITY_ENDPOINT,
  JACK_HENRY_SYNERGY_PRODUCT_ID
} from '../types/jack-henry-constants'
import {
  OE_HEADER_APPLICATION_ID,
  OE_HEADER_AUDIT_USER_ID,
  OE_HEADER_AUDIT_WORKSTATION_ID,
  OE_HEADER_BASE_URL,
  OE_HEADER_CONFIG_ID,
  OE_HEADER_ENTITY_ID,
  OE_HEADER_INSTITUTION_ENVIRONMENT,
  OE_HEADER_INSTITUTION_ID,
  OE_HEADER_MEMBER_NUMBER,
  OE_HEADER_MODEL_NUMBER,
  OE_HEADER_OAUTH_CLIENT_ASSERTION,
  OE_HEADER_OAUTH_CLIENT_ID,
  OE_HEADER_OAUTH_CLIENT_SECRET,
  OE_HEADER_ROUTING_ID,
  OE_HEADER_SECURITY_CODE,
  OE_HEADER_TENANT_ID,
  OE_HEADER_VALID_CONSUMER_NAME,
  OE_HEADER_VALID_CONSUMER_PRODUCT
} from './oe-headers'

export type { IntegrationProviderId } from '../types/integration-provider-id'

export interface IntegrationOverrideFieldDef {
  key: string
  header: string
  dbPath?: string
  sensitive?: boolean
  requiredAfterResolve?: boolean
  headerOnly?: boolean
}

export interface IntegrationProviderDefinition {
  id: IntegrationProviderId
  defaults: Record<string, string>
  overrideFields: IntegrationOverrideFieldDef[]
  detectConfigured: (integration: Integration) => boolean
}

export type ResolvedSilverlakeConfig = {
  oauthClientId: string
  privateKey?: string
  oauthClientAssertion?: string
  scope: string
  routingId: string
  institutionEnvironment: string
  validConsumerName: string
  validConsumerProduct: string
  auditUserId: string
  auditWorkstationId: string
  tokenUrl: string
  baseUrl: string
  achXferDebitTransactionCode?: string
  achXferCreditTransactionCode?: string
}

export type ResolvedSynergyConfig = {
  oauthClientId: string
  oauthClientSecret: string
  scope: string
  institutionId: string
  institutionEnvironment: string
  baseUrl: string
  tokenUrl: string
  productId: string
}

export type ResolvedEquifaxPrequalConfig = {
  oauthClientId: string
  oauthClientSecret: string
  baseUrl: string
  memberNo: string
  securityCode: string
  modelNo: string
}

export type ResolvedEquifaxDitConfig = {
  oauthClientId: string
  oauthClientSecret: string
  baseUrl: string
  tenantId: string
  applicationId: string
  configId: string
  entityId: string
}

export type ResolvedProviderConfig =
  | ResolvedSilverlakeConfig
  | ResolvedSynergyConfig
  | ResolvedEquifaxPrequalConfig
  | ResolvedEquifaxDitConfig

const SILVERLAKE_OVERRIDE_FIELDS: IntegrationOverrideFieldDef[] = [
  {
    key: 'oauthClientId',
    header: OE_HEADER_OAUTH_CLIENT_ID,
    dbPath: 'jackHenrySilverlakeConfig.oauthClientId',
    requiredAfterResolve: true
  },
  {
    key: 'oauthClientAssertion',
    header: OE_HEADER_OAUTH_CLIENT_ASSERTION,
    sensitive: true,
    headerOnly: true
  },
  {
    key: 'privateKey',
    header: '',
    dbPath: 'jackHenrySilverlakeConfig.privateKey',
    sensitive: true
  },
  {
    key: 'scope',
    header: '',
    dbPath: 'jackHenrySilverlakeConfig.scope',
    requiredAfterResolve: true
  },
  {
    key: 'routingId',
    header: OE_HEADER_ROUTING_ID,
    dbPath: 'jackHenrySilverlakeConfig.routingId',
    requiredAfterResolve: true
  },
  {
    key: 'institutionEnvironment',
    header: OE_HEADER_INSTITUTION_ENVIRONMENT,
    dbPath: 'jackHenrySilverlakeConfig.institutionEnvironment',
    requiredAfterResolve: true
  },
  {
    key: 'validConsumerName',
    header: OE_HEADER_VALID_CONSUMER_NAME,
    dbPath: 'jackHenrySilverlakeConfig.validConsumerName',
    requiredAfterResolve: true
  },
  {
    key: 'validConsumerProduct',
    header: OE_HEADER_VALID_CONSUMER_PRODUCT,
    dbPath: 'jackHenrySilverlakeConfig.validConsumerProduct',
    requiredAfterResolve: true
  },
  {
    key: 'auditUserId',
    header: OE_HEADER_AUDIT_USER_ID,
    dbPath: 'jackHenrySilverlakeConfig.auditUserId',
    requiredAfterResolve: true
  },
  {
    key: 'auditWorkstationId',
    header: OE_HEADER_AUDIT_WORKSTATION_ID,
    dbPath: 'jackHenrySilverlakeConfig.auditWorkstationId',
    requiredAfterResolve: true
  },
  {
    key: 'achXferDebitTransactionCode',
    header: '',
    dbPath: 'jackHenrySilverlakeConfig.achXferDebitTransactionCode'
  },
  {
    key: 'achXferCreditTransactionCode',
    header: '',
    dbPath: 'jackHenrySilverlakeConfig.achXferCreditTransactionCode'
  }
]

const SYNERGY_OVERRIDE_FIELDS: IntegrationOverrideFieldDef[] = [
  {
    key: 'oauthClientId',
    header: OE_HEADER_OAUTH_CLIENT_ID,
    dbPath: 'jackHenrySynergyConfig.clientId',
    requiredAfterResolve: true
  },
  {
    key: 'oauthClientSecret',
    header: OE_HEADER_OAUTH_CLIENT_SECRET,
    dbPath: 'jackHenrySynergyConfig.clientSecret',
    sensitive: true,
    requiredAfterResolve: true
  },
  {
    key: 'scope',
    header: '',
    dbPath: 'jackHenrySynergyConfig.scope'
  },
  {
    key: 'institutionId',
    header: OE_HEADER_INSTITUTION_ID,
    dbPath: 'jackHenrySynergyConfig.institutionId',
    requiredAfterResolve: true
  },
  {
    key: 'institutionEnvironment',
    header: OE_HEADER_INSTITUTION_ENVIRONMENT,
    dbPath: 'jackHenrySynergyConfig.institutionEnvironment',
    requiredAfterResolve: true
  }
]

const EQUIFAX_PREQUAL_OVERRIDE_FIELDS: IntegrationOverrideFieldDef[] = [
  {
    key: 'oauthClientId',
    header: OE_HEADER_OAUTH_CLIENT_ID,
    dbPath: 'equifaxPrequalConfig.clientId',
    requiredAfterResolve: true
  },
  {
    key: 'oauthClientSecret',
    header: OE_HEADER_OAUTH_CLIENT_SECRET,
    dbPath: 'equifaxPrequalConfig.clientSecret',
    sensitive: true,
    requiredAfterResolve: true
  },
  {
    key: 'baseUrl',
    header: OE_HEADER_BASE_URL,
    dbPath: 'equifaxPrequalConfig.baseUrl',
    requiredAfterResolve: true
  },
  {
    key: 'memberNo',
    header: OE_HEADER_MEMBER_NUMBER,
    dbPath: 'equifaxPrequalConfig.memberNo',
    requiredAfterResolve: true
  },
  {
    key: 'securityCode',
    header: OE_HEADER_SECURITY_CODE,
    dbPath: 'equifaxPrequalConfig.securityCode',
    requiredAfterResolve: true
  },
  {
    key: 'modelNo',
    header: OE_HEADER_MODEL_NUMBER,
    dbPath: 'equifaxPrequalConfig.modelNo',
    requiredAfterResolve: true
  }
]

const EQUIFAX_DIT_OVERRIDE_FIELDS: IntegrationOverrideFieldDef[] = [
  {
    key: 'oauthClientId',
    header: OE_HEADER_OAUTH_CLIENT_ID,
    dbPath: 'equifaxDITConfig.clientId',
    requiredAfterResolve: true
  },
  {
    key: 'oauthClientSecret',
    header: OE_HEADER_OAUTH_CLIENT_SECRET,
    dbPath: 'equifaxDITConfig.clientSecret',
    sensitive: true,
    requiredAfterResolve: true
  },
  {
    key: 'baseUrl',
    header: OE_HEADER_BASE_URL,
    dbPath: 'equifaxDITConfig.baseUrl',
    requiredAfterResolve: true
  },
  {
    key: 'tenantId',
    header: OE_HEADER_TENANT_ID,
    dbPath: 'equifaxDITConfig.tenantId',
    requiredAfterResolve: true
  },
  {
    key: 'applicationId',
    header: OE_HEADER_APPLICATION_ID,
    dbPath: 'equifaxDITConfig.applicationId',
    requiredAfterResolve: true
  },
  {
    key: 'configId',
    header: OE_HEADER_CONFIG_ID,
    dbPath: 'equifaxDITConfig.configId',
    requiredAfterResolve: true
  },
  {
    key: 'entityId',
    header: OE_HEADER_ENTITY_ID,
    dbPath: 'equifaxDITConfig.entityId',
    requiredAfterResolve: true
  }
]

function silverlakeConfigured(integration: Integration): boolean {
  return integrationHasProvider(integration, 'jack_henry_silverlake')
}

function synergyConfigured(integration: Integration): boolean {
  return integrationHasProvider(integration, 'jack_henry_synergy')
}

function equifaxPrequalConfigured(integration: Integration): boolean {
  return integrationHasProvider(integration, 'equifax_prequal')
}

function equifaxDitConfigured(integration: Integration): boolean {
  return integrationHasProvider(integration, 'equifax_dit')
}

export const INTEGRATION_PROVIDER_DEFINITIONS: Record<
  IntegrationProviderId,
  IntegrationProviderDefinition
> = {
  jack_henry_silverlake: {
    id: 'jack_henry_silverlake',
    defaults: {
      tokenUrl: JACK_HENRY_CORE_TOKEN_URL,
      baseUrl: JACK_HENRY_CORE_SERVICE_GATEWAY_BASE_URL
    },
    overrideFields: SILVERLAKE_OVERRIDE_FIELDS,
    detectConfigured: silverlakeConfigured
  },
  jack_henry_synergy: {
    id: 'jack_henry_synergy',
    defaults: {
      baseUrl: JACK_HENRY_SYNERGY_BASE_URL,
      tokenUrl: JACK_HENRY_SYNERGY_IDENTITY_ENDPOINT,
      productId: JACK_HENRY_SYNERGY_PRODUCT_ID,
      scope: JACK_HENRY_SYNERGY_OAUTH_SCOPE
    },
    overrideFields: SYNERGY_OVERRIDE_FIELDS,
    detectConfigured: synergyConfigured
  },
  equifax_prequal: {
    id: 'equifax_prequal',
    defaults: {},
    overrideFields: EQUIFAX_PREQUAL_OVERRIDE_FIELDS,
    detectConfigured: equifaxPrequalConfigured
  },
  equifax_dit: {
    id: 'equifax_dit',
    defaults: {},
    overrideFields: EQUIFAX_DIT_OVERRIDE_FIELDS,
    detectConfigured: equifaxDitConfigured
  }
}

export const SUPPORTED_INTEGRATION_PROVIDER_IDS = Object.keys(
  INTEGRATION_PROVIDER_DEFINITIONS
) as IntegrationProviderId[]

export function getIntegrationProviderDefinition(
  providerId: IntegrationProviderId
): IntegrationProviderDefinition {
  return INTEGRATION_PROVIDER_DEFINITIONS[providerId]
}

export function getProviderOverrideHeaders(
  providerId: IntegrationProviderId
): IntegrationOverrideFieldDef[] {
  return getIntegrationProviderDefinition(providerId).overrideFields.filter(
    (field) => field.header.length > 0
  )
}

export function getProviderRequiredHeaders(
  providerId: IntegrationProviderId
): string[] {
  return getProviderOverrideHeaders(providerId)
    .filter((field) => field.requiredAfterResolve)
    .map((field) => field.header)
}

export function toResolvedSilverlakeConfig(
  resolved: Record<string, string>
): ResolvedSilverlakeConfig {
  return {
    oauthClientId: resolved.oauthClientId ?? '',
    privateKey: resolved.privateKey,
    oauthClientAssertion: resolved.oauthClientAssertion,
    scope: resolved.scope ?? '',
    routingId: resolved.routingId ?? '',
    institutionEnvironment: resolved.institutionEnvironment ?? '',
    validConsumerName: resolved.validConsumerName ?? '',
    validConsumerProduct: resolved.validConsumerProduct ?? '',
    auditUserId: resolved.auditUserId ?? '',
    auditWorkstationId: resolved.auditWorkstationId ?? '',
    tokenUrl: resolved.tokenUrl ?? '',
    baseUrl: resolved.baseUrl ?? '',
    achXferDebitTransactionCode: resolved.achXferDebitTransactionCode,
    achXferCreditTransactionCode: resolved.achXferCreditTransactionCode
  }
}

export function toResolvedSynergyConfig(resolved: Record<string, string>): ResolvedSynergyConfig {
  return {
    oauthClientId: resolved.oauthClientId ?? '',
    oauthClientSecret: resolved.oauthClientSecret ?? '',
    scope: resolved.scope ?? '',
    institutionId: resolved.institutionId ?? '',
    institutionEnvironment: resolved.institutionEnvironment ?? '',
    baseUrl: resolved.baseUrl ?? '',
    tokenUrl: resolved.tokenUrl ?? '',
    productId: resolved.productId ?? ''
  }
}

function resolvedEquifaxBaseUrl(resolved: Record<string, string>): string {
  return resolveEquifaxBaseUrl(resolved.baseUrl ?? '') ?? ''
}

export function toResolvedEquifaxPrequalConfig(
  resolved: Record<string, string>
): ResolvedEquifaxPrequalConfig {
  return {
    oauthClientId: resolved.oauthClientId ?? '',
    oauthClientSecret: resolved.oauthClientSecret ?? '',
    baseUrl: resolvedEquifaxBaseUrl(resolved),
    memberNo: resolved.memberNo ?? '',
    securityCode: resolved.securityCode ?? '',
    modelNo: resolved.modelNo ?? ''
  }
}

export function toResolvedEquifaxDitConfig(
  resolved: Record<string, string>
): ResolvedEquifaxDitConfig {
  return {
    oauthClientId: resolved.oauthClientId ?? '',
    oauthClientSecret: resolved.oauthClientSecret ?? '',
    baseUrl: resolvedEquifaxBaseUrl(resolved),
    tenantId: resolved.tenantId ?? '',
    applicationId: resolved.applicationId ?? '',
    configId: resolved.configId ?? '',
    entityId: resolved.entityId ?? ''
  }
}

export type IntegrationConfigBlocks = {
  jackHenrySilverlakeConfig?: IJackHenrySilverlakeIntegrationConfig
  jackHenrySynergyConfig?: IJackHenrySynergyIntegrationConfig
  equifaxPrequalConfig?: IEquifaxPrequalConfig
  equifaxDITConfig?: IEquifaxDitConfig
}
